import { useCallback, useEffect, useState } from 'react'
import { getAppBearer } from '@/lib/sdk-session-token'
import { localCopyOf } from '@/lib/local-media-copies'
import {
  forgetResolvedAttachment,
  isPrivateAttachmentUrl,
  resolveAttachmentUrl,
  type ResolveOptions,
} from '../services/private-attachment'

/** Attachment URLs served by the inMeet backend, which are behind bearer auth.
 *  Kept identical to the check in `useAttachmentSrc`: both hooks resolve the
 *  same URLs the same way, this one just also reports failure instead of
 *  swallowing it. */
function needsAuth(url: string): boolean {
  return url.includes('/api/v1/attachments/')
}

export type AttachmentSrcStatus = 'loading' | 'ready' | 'failed'

export interface AttachmentSrcState {
  src: string | undefined
  status: AttachmentSrcStatus
  /** Re-runs the fetch for an auth URL. For a public URL there is nothing to
   *  re-fetch (it already passed straight through), so a caller showing a
   *  retry affordance after the `<img>` itself fired `onError` should pair
   *  this with its own cache-busting remount, not rely on it alone. */
  retry: () => void
}

/**
 * Same resolver as `useAttachmentSrc`, but one that tells the caller *why*
 * there is no src yet instead of collapsing "still loading" and "the fetch
 * failed" into the same `undefined`. A caller rendering a loading spinner for
 * that `undefined` showed the same spinner forever on a failed photo: there
 * was no way to tell the two apart.
 */
export function useAttachmentSrcState(url?: string | null, opts: ResolveOptions = {}): AttachmentSrcState {
  const meetingId = opts.meetingId
  const [state, setState] = useState<{ src: string | undefined; status: AttachmentSrcStatus }>(() => {
    const local = localCopyOf(url)
    if (local) return { src: local, status: 'ready' }
    return url
      ? needsAuth(url) || isPrivateAttachmentUrl(url)
        ? { src: undefined, status: 'loading' }
        : { src: url, status: 'ready' }
      : { src: undefined, status: 'ready' }
  })
  // Bumped by `retry()` to force the effect below to run again against the
  // same url: a plain state flip wouldn't, since `url` itself hasn't changed.
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!url) {
      setState({ src: undefined, status: 'ready' })
      return
    }
    // A picture this tab just sent: shown from the sender's own file.
    const local = localCopyOf(url)
    if (local) {
      setState({ src: local, status: 'ready' })
      return
    }
    // A private chat file (VAPT 2026-10): a link signed for this user, which
    // the element loads as it is. Refused reads as failed, so Retry is offered.
    if (isPrivateAttachmentUrl(url)) {
      let cancelled = false
      setState({ src: undefined, status: 'loading' })
      resolveAttachmentUrl(url, { meetingId }).then(
        (signed) => { if (!cancelled) setState({ src: signed, status: 'ready' }) },
        () => { if (!cancelled) setState({ src: undefined, status: 'failed' }) },
      )
      return () => { cancelled = true }
    }
    if (!needsAuth(url)) {
      setState({ src: url, status: 'ready' })
      return
    }

    setState({ src: undefined, status: 'loading' })
    let objectUrl: string | null = null
    const controller = new AbortController()

    void (async () => {
      try {
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getAppBearer()}` },
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(String(res.status))
        objectUrl = URL.createObjectURL(await res.blob())
        setState({ src: objectUrl, status: 'ready' })
      } catch {
        // Reported as failed rather than left undefined forever: the whole
        // point of this hook over `useAttachmentSrc` is that a caller can
        // tell this apart from still-loading and offer a retry.
        if (!controller.signal.aborted) setState({ src: undefined, status: 'failed' })
      }
    })()

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [url, attempt, meetingId])

  // A retry asks for a fresh link too: the one held may be what failed.
  const retry = useCallback(() => {
    if (url) forgetResolvedAttachment(url)
    setAttempt(a => a + 1)
  }, [url])

  return { src: state.src, status: state.status, retry }
}
